import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { api } from "./api";
import { ProgramLeaveType, ProgramSession, ProgramSessionEnrollment } from "../models/programs.models";
import { ProgramRegistration } from "../models/programregistration.models";

export interface ProgramSessionEnrollmentRegistration {
	ProgramSessionEnrollment: ProgramSessionEnrollment;
	Registration: ProgramRegistration;
	ProgramCanLeave: ProgramLeaveType;
}

export const useProgramSessionByIdQuery = (programSessionId: number) => {
	const hasAccess = useAccess("role.user.readonly");
	const accountId = useAccountId();

	return useQuery(['program-session-by-id', programSessionId, accountId], async () => {
		let res = await api.get<ProgramSession>(`/admin/programs/sessions/${programSessionId}`);
		return res.data;
	}, { staleTime: Infinity, cacheTime: 60000, enabled: hasAccess && programSessionId > 0 });
}

export const useProgramSessionEnrollmentsByIdQuery = (programSessionId: number, autoRefetch: boolean = false) => {
	const hasAccess = useAccess("role.user.readonly");
	const accountId = useAccountId();

	return useQuery(['program-session-enrollments-by-id', programSessionId, accountId], async () => {
		let res = await api.get<ProgramSessionEnrollmentRegistration[]>(`/admin/programs/sessions/${programSessionId}/enrollments`);
		return res.data;
	}, { staleTime: Infinity, cacheTime: 60000, enabled: hasAccess && programSessionId > 0, refetchInterval: autoRefetch ? 30000 : false });
}

export const useProgramSessionEnrollmentsByRegistrationIdQuery = (registrationId: number) => {
	const hasAccess = useAccess("role.user.readonly");
	const accountId = useAccountId();

	return useQuery(['program-session-enrollments-by-registration-id', registrationId, accountId], async () => {
		let res = await api.get<ProgramSessionEnrollment[]>(`/admin/programs/sessions/registration/${registrationId}/enrollments`);
		return res.data;
	}, { staleTime: Infinity, cacheTime: 60000, enabled: hasAccess && registrationId > 0 });
}

export const useProgramSessionEnrollmentCheckInMutation = (programSessionId: number) => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation((model: { programSessionEnrollmentId: number, checkedIn: boolean, canLeave?: boolean }) => {
		return api.post<ProgramSessionEnrollment>(`/admin/programs/sessions/enrollments/${model.programSessionEnrollmentId}/checkin`, { checkedIn: model.checkedIn, canLeave: model.canLeave });
	}, {
		onMutate: async (model) => {
			const queryKey = ['program-session-enrollments-by-id', programSessionId, accountId] as const;
			await queryClient.cancelQueries(queryKey);

			const previousData = queryClient.getQueryData<ProgramSessionEnrollmentRegistration[]>(queryKey);
			queryClient.setQueryData<ProgramSessionEnrollmentRegistration[]>(queryKey, (current) => {
				if (!current)
					return [];

				return current.map((enrollment) => {
					if (enrollment.ProgramSessionEnrollment.Id !== model.programSessionEnrollmentId)
						return enrollment;

					return {
						...enrollment,
						ProgramSessionEnrollment: {
							...enrollment.ProgramSessionEnrollment,
							CheckedIn: model.checkedIn,
							CanLeave: model.canLeave ?? enrollment.ProgramSessionEnrollment.CanLeave
						}
					};
				});
			});

			return { queryKey, previousData };
		},
		onError: (_error, _model, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(context.queryKey, context.previousData);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries(['program-session-enrollments-by-id', programSessionId, accountId]);
		}
	});
}

export const useProgramSessionEnrollmentCheckOutMutation = (programSessionId: number) => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation((model: { programSessionEnrollmentId: number, checkedOut: boolean }) => {
		return api.post<ProgramSessionEnrollment>(`/admin/programs/sessions/enrollments/${model.programSessionEnrollmentId}/checkout`, { checkedOut: model.checkedOut });
	}, {
		onMutate: async (model) => {
			const queryKey = ['program-session-enrollments-by-id', programSessionId, accountId] as const;
			await queryClient.cancelQueries(queryKey);

			const previousData = queryClient.getQueryData<ProgramSessionEnrollmentRegistration[]>(queryKey);
			queryClient.setQueryData<ProgramSessionEnrollmentRegistration[]>(queryKey, (current) => {
				if (!current)
					return [];

				return current.map((enrollment) => {
					if (enrollment.ProgramSessionEnrollment.Id !== model.programSessionEnrollmentId)
						return enrollment;

					return {
						...enrollment,
						ProgramSessionEnrollment: {
							...enrollment.ProgramSessionEnrollment,
							CheckedOut: model.checkedOut
						}
					};
				});
			});

			return { queryKey, previousData };
		},
		onError: (_error, _model, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(context.queryKey, context.previousData);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries(['program-session-enrollments-by-id', programSessionId, accountId]);
		}
	});
}